import { useEffect, useRef, useState } from "react";
import styles from "./Notifications.module.scss";
import { NotificationsService } from "../../../services/notifications.service";
import { NotificationsDataType } from "../../../interface/site.interface";
import Spinner from "../../../components/shared/spinner/Spinner";
import { Link } from "react-router-dom";
import TitleUpdater from "../../../components/shared/title/TitleUpdater";
import { useDispatch, useSelector } from "react-redux";
import { fetchUser } from "../../../redux/Slices/userProfile";
import { AppDispatch, RootState } from "../../../redux/store";
import MainButton from "../../../components/shared/button/MainButton";
import { fetchTranslation } from "../../../redux/Slices/translationDataSlice";

interface PaginationData {
  current_page: number;
  first_page_url: string;
  from: number;
  last_page: number;
  last_page_url: string;
  next_page_url: string | null;
  path: string;
  per_page: number;
  prev_page_url: string | null;
  to: number;
  total: number;
}

const initialPaginationState: PaginationData = {
  current_page: 1,
  first_page_url: "",
  from: 0,
  last_page: 1,
  last_page_url: "",
  next_page_url: null,
  path: "",
  per_page: 15,
  prev_page_url: null,
  to: 0,
  total: 0,
};

const Notifications = () => {
  const [notifications, setNotifications] = useState<NotificationsDataType[]>(
    []
  );
  const [loading, setLoading] = useState<boolean>(false);
  const [pagination, setPagination] = useState<PaginationData>(
    initialPaginationState
  );
  const { data } = useSelector((state: RootState) => state.translationData);

  const language = useSelector((state: RootState) => state.language.language);
  const [loadingMore, setLoadingMore] = useState<boolean>(false);
  const dispatch = useDispatch<AppDispatch>();

  const markAllAsRead = () => {
    const notificationIds = notifications.map((el) => el.id);
    const data = { notification: notificationIds };
    NotificationsService.readAllNotification(data).then(() => {
      setNotifications((prevNotifications) =>
        prevNotifications.map((notification) => ({
          ...notification,
          is_read: true,
        }))
      );
      dispatch(fetchUser());
    });
  };

  const initialRender = useRef(true);

  useEffect(() => {
    if (initialRender.current) {
      initialRender.current = false;
      if (!data) {
        dispatch(fetchTranslation());
      }
    } else {
      dispatch(fetchTranslation());
    }
  }, [dispatch, language]);

  const fetchNotifications = (url?: string) => {
    const isInitialLoad = !url;
    isInitialLoad ? setLoading(true) : setLoadingMore(true);

    NotificationsService.getAllNotifications(url || "")
      .then((res) => {
        if (!res?.data) return;

        const response = res.data;

        if (isInitialLoad) {
          setNotifications(response?.data);
          setPagination({
            current_page: response?.current_page,
            first_page_url: response?.first_page_url,
            from: response?.from,
            last_page: response?.last_page,
            last_page_url: response?.last_page_url,
            next_page_url: response?.next_page_url,
            path: response?.path,
            per_page: response?.per_page,
            prev_page_url: response?.prev_page_url,
            to: response?.to,
            total: response?.total,
          });
        } else {
          setNotifications((prev) => [...prev, ...response.data]);
          setPagination((prev) => ({
            ...prev,
            current_page: response.current_page,
            next_page_url: response.next_page_url,
          }));
        }
      })
      .finally(() => {
        isInitialLoad ? setLoading(false) : setLoadingMore(false);
      });
  };

  const loadMoreNotifications = () => {
    if (pagination.next_page_url) {
      fetchNotifications(pagination.next_page_url);
    }
  };

  useEffect(() => {
    fetchNotifications();
  }, [language]);

  useEffect(() => {
    const unreadCount = notifications.filter((n) => !n.is_read).length;
    if (unreadCount === 0 && notifications.length > 0) {
      dispatch(fetchUser());
    }
  }, [notifications, dispatch]);

  return (
    <div id={styles.notifications}>
      <TitleUpdater
        title={
          data?.colibri && data?.auth_common_notifications
            ? `${data?.colibri} | ${data?.auth_common_notifications}`
            : "Colibri"
        }
      />
      {loading ? (
        <Spinner size="large" />
      ) : notifications?.length === 0 ? (
        <p className={styles.no_data}>{data?.no_notifications}</p>
      ) : (
        <div className={styles.notifications}>
          <div className={styles.head}>
            <div className={styles.read_all_notify} onClick={markAllAsRead}>
              {data?.mark_all_read}
            </div>
          </div>
          <div className={styles.nofications_wrapper}>
            {notifications.map((note, index) => (
              <div
                key={index}
                className={`${styles.note_card} ${
                  !note.is_read ? styles.active_note : ""
                }`}
              >
                <Link to={`/account/notifications/${note.id}`}>
                  <p>{note.subject_header}</p>
                </Link>
              </div>
            ))}
          </div>
          {pagination.next_page_url && (
            <div className={styles.more_btn}>
              <MainButton
                text={loadingMore ? "Yüklənir" : "Daha çox"}
                onClick={loadMoreNotifications}
              />
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default Notifications;
